import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { catchError, debounceTime, distinctUntilChanged, finalize, of, switchMap, tap } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzValidateStatus } from 'ng-zorro-antd/core/types'; 

import { PermissionEnum, UserDto, UserStatusEnum } from "../../../dto";
import { HttpUserService } from "../../../services/user/http-user.service";
import { AuthService } from "../../../services/auth/auth.service";
import { UnauthorizedAccessEditWarningComponent } from "../../../components/unauthorized-access-edit-warning/unauthorized-access-edit-warning.component";

@Component({
    selector: 'app-edit-user-modal',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule,
        NzModalModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule,
        NzSelectModule,
        UnauthorizedAccessEditWarningComponent
    ],
    templateUrl: './edit-user-modal.component.html',
    styleUrls: ['./edit-user-modal.component.scss']
})
export class EditUserModalComponent implements OnInit {
    @Input() modalId = 'editUserModal';
    @Output() saved = new EventEmitter<UserDto>();
    @Output() cancelled = new EventEmitter<void>();

    user!: UserDto | undefined;
    form!: FormGroup;
    statuses = Object.values(UserStatusEnum);
    
    // UI State
    isVisible = false;
    isConfirmLoading = false; // <--- Controls Button Spinner

    usernameStatus: 'idle' | 'checking' | 'available' | 'taken' = 'idle';

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private httpService: HttpUserService,
        private authService: AuthService
    ) { }

    get canEdit(): boolean {
        return this.authService.hasAccess(PermissionEnum.UsersEdit);
    }

    get currentValidateStatus(): NzValidateStatus {
        if (this.usernameStatus === 'checking') return 'validating';
        if (this.usernameStatus === 'available') return 'success';
        if (this.usernameStatus === 'taken') return 'error';
        
        if (this.form.get('newUsername')?.invalid && (this.form.get('newUsername')?.dirty || this.form.get('newUsername')?.touched)) {
            return 'error';
        }
        return '';
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            newUsername: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]],
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(20)]],
            surname: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20)]],
            email: ['', [Validators.email]],
            status: [UserStatusEnum.Active, Validators.required],
        });

        this.form.get('newUsername')?.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                tap(value => {
                    if (!value || value.trim() === '') {
                        this.usernameStatus = 'idle';
                    }
                }),
                switchMap(value => {
                    if (!value || value.trim() === '') {
                        this.form.get('newUsername')?.setErrors({ required: true });
                        return of(null);
                    }
                    this.usernameStatus = 'checking';
                    return this.httpService.checkUsername({ id: this.user?.id, username: value });
                })
            )
            .subscribe(data => {
                const control = this.form.get('newUsername');
                if (!control) return;

                if (!data) return;

                if (!data.success) {
                    control.setErrors({ usernameTaken: true });
                    this.usernameStatus = 'taken';
                } else {
                    control.setErrors(null);
                    this.usernameStatus = 'available';
                }
            });
    }

    open(user: UserDto) {
        this.user = user;
        this.form.reset(); 
        this.usernameStatus = 'idle';

        this.form.patchValue(this.user);
        this.form.get('newUsername')?.setValue(this.user.username);

        this.isVisible = true;

        if (!this.canEdit) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    handleCancel(): void {
        this.isVisible = false;
        this.cancelled.emit();
    }

    save(): void {
        this.form.markAllAsTouched();
        if (this.form.valid) {
            
            // START BUTTON SPINNER
            this.isConfirmLoading = true;

            const formData = this.form.value;

            var userDto: UserDto = {
                id: this.user?.id,
                username: formData.newUsername,
                name: formData.name,
                surname: formData.surname,
                email: formData.email,
                status: formData.status
            }

            this.httpService.saveUser(userDto)
                .pipe(
                    catchError(error => {
                        console.log('Error editing user', error);
                        this.toastr.error('Failed to edit user: ' + (error.error ? error.error.error : error.message));
                        throw error;
                    }),
                    // STOP BUTTON SPINNER
                    finalize(() => this.isConfirmLoading = false)
                )
                .subscribe(data => {
                    if (!data.success) {
                        this.toastr.error('Failed to edit user: ' + data.error);
                        return;
                    }

                    this.saved.emit(userDto);
                    this.isVisible = false;
                    this.toastr.success('User saved successfully');
                });
        }
    }
}